import React, { useEffect, useState } from "react";
import { Paper, Stack, Typography, Chip, Box } from "@mui/material";
import { apiCallUserPreferences } from "../../../../Core/Apicall";
import CommonShimmer from "../../../Common/CommonShimmer/CommonShimmer";
import { useLocation } from "react-router-dom";

interface PreferenceData {
  daysIds: string[] | { _id: string; dayName: string }[];
  regionsIds?: { _id: string; regionName: string }[];
  venueTagIds?: { _id: string; venueTagName: string }[];
  liveMusicIds?: { _id: string; liveMusicName: string }[];
  djClubIds?: { _id: string; djClubName: string }[];
  entryFeeIds?: { _id: string; entryFeeName: string }[];
  crowdIds?: { _id: string; crowdName: string }[];
  ageGroupIds?: { _id: string; ageGroupName: string }[];
  eventActivitiesIds?: { _id: string; eventActivityName: string }[];
  alcoholIds?: { _id: string; alcoholName: string }[];
  nonAlcoholIds?: { _id: string; nonAlcoholName: string }[];
  specialIds?: { _id: string; specialName: string }[];
  cuisineIds?: { _id: string; cuisineName: string }[];
  cravingIds?: { _id: string; cravingName: string }[];
  foodPriceIds?: { _id: string; foodPriceName: string }[];
  dietaryIds?: { _id: string; dietaryName: string }[];
}

const preferenceConfig = [
  { field: "daysIds", label: "Days", nameKey: "dayName" },
  { field: "regionsIds", label: "Regions", nameKey: "regionName" },
  { field: "venueTagIds", label: "Venue Tags", nameKey: "venueTagName" },
  { field: "liveMusicIds", label: "Live Music", nameKey: "liveMusicName" },
  { field: "djClubIds", label: "DJ/Club", nameKey: "djClubName" },
  { field: "entryFeeIds", label: "Entry Fee", nameKey: "entryFeeName" },
  { field: "crowdIds", label: "Crowd", nameKey: "crowdName" },
  { field: "ageGroupIds", label: "Age Group", nameKey: "ageGroupName" },
  { field: "eventActivitiesIds", label: "Event Activities", nameKey: "eventActivityName" },
  { field: "alcoholIds", label: "Alcohol", nameKey: "alcoholName" },
  { field: "nonAlcoholIds", label: "Non-Alcohol", nameKey: "nonAlcoholName" },
  { field: "specialIds", label: "Special", nameKey: "specialName" },
  { field: "cuisineIds", label: "Cuisine", nameKey: "cuisineName" },
  { field: "cravingIds", label: "Craving", nameKey: "cravingName" },
  { field: "foodPriceIds", label: "Food Price", nameKey: "foodPriceName" },
  { field: "dietaryIds", label: "Dietary", nameKey: "dietaryName" },
];

const UserPreferences: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [preferences, setPreferences] = useState<PreferenceData | null>(null);

  const location = useLocation();
  const userData = location.state?.user;

  const fetchUserPreferences = async (userId: any) => {
    try {
      setLoading(true);
      const res = await apiCallUserPreferences({ userId });

      if (res?.data) {
        setPreferences(res.data);
      }
    } catch (error) {
      console.error("Error fetching preferences:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userData) {
      fetchUserPreferences(userData.userId ? userData.userId : userData._id);
    }
  }, [userData]);

  const renderChips = (items: any[] | undefined, labelKey: string) => {
    if (!items || items.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No preferences selected
        </Typography>
      );
    }

    return (
      <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
        {items.map((item, index) => {
          let label = "";
          let key = "";

          if (typeof item === "string") {
            label = item;
            key = item;
          } else if (typeof item === "object" && item !== null) {
            label = item[labelKey] || item._id || "Unknown";
            key = item._id || index.toString();
          }

          return (
            <Chip
              key={key}
              label={label}
              sx={{ bgcolor: "black", color: "white" }}
            />
          );
        })}
      </Stack>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: "16px",
        textAlign: "center",
        maxWidth: "100%",
        width: "100%",
        backgroundColor: "transparent",
        maxHeight: "500px",
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
          borderRadius: "3px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#888",
          borderRadius: "3px",
          "&:hover": {
            background: "#555",
          },
        },
      }}
    >
      {loading ? (
        <CommonShimmer />
      ) : (
        <div
          style={{
            display: "block",
            justifyContent: "left",
            width: "100%",
            gap: "30px",
            padding: "5px",
            flexWrap: "wrap",
          }}
        >
          {preferenceConfig.map(({ field, label, nameKey }) => {
            const items = preferences?.[field as keyof PreferenceData] as
              | any[]
              | undefined;
            return (
              <div key={field}>
                <Typography sx={{ textAlign: "left", mb: 1 }}>
                  {label}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                  {renderChips(items, nameKey)}
                </Box>
              </div>
            );
          })}
        </div>
      )}
    </Paper>
  );
};

export default UserPreferences;